import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

/** Sent on Insert/Update. effectiveDate/endDate may be null (see field rules). */
export interface PremiumRuleDto {
  id?: number;
  masterPolicyId: number;
  coverageType: string;
  premiumCode: string;
  ageFrom: number;
  ageTo: number;
  basePremium: number;
  gstPercentage: number;
  /** Doctor Consultation add-on price, PRE-GST (0 = none). Lives on the rule — the benefit
   *  breakup row only mirrors it. */
  doctorConsultant?: number;
  policyTermYears: number; // coverage duration in years (>= 1); drives the COI Period of Insurance
  effectiveDate?: string | null; // null => server defaults to today
  endDate?: string | null; // null => never expires
  status: string; // ACTIVE | INACTIVE
}

/** Shape returned on list/detail (joined masterPolicyName + audit fields). */
export interface PremiumRule extends PremiumRuleDto {
  id: number;
  masterPolicyName?: string | null;
  createdAt?: string | null;
}

/** GetAll/GetById/GetByMasterPolicy may return PascalCase; normalize to the camelCase model. */
function mapRule(x: any): PremiumRule {
  return {
    id: x.Id ?? x.id,
    masterPolicyId: x.MasterPolicyId ?? x.masterPolicyId,
    masterPolicyName: x.MasterPolicyName ?? x.masterPolicyName ?? null,
    coverageType: x.CoverageType ?? x.coverageType,
    premiumCode: x.PremiumCode ?? x.premiumCode,
    ageFrom: x.AgeFrom ?? x.ageFrom,
    ageTo: x.AgeTo ?? x.ageTo,
    basePremium: x.BasePremium ?? x.basePremium,
    gstPercentage: x.GstPercentage ?? x.gstPercentage,
    doctorConsultant: x.DoctorConsultant ?? x.doctorConsultant ?? 0,
    policyTermYears: x.PolicyTermYears ?? x.policyTermYears ?? 1,
    effectiveDate: x.EffectiveDate ?? x.effectiveDate ?? null,
    endDate: x.EndDate ?? x.endDate ?? null,
    status: x.Status ?? x.status,
    createdAt: x.CreatedAt ?? x.createdAt ?? null,
  };
}

function mapEnvelope(res: any) {
  if (Array.isArray(res?.data)) return { ...res, data: res.data.map(mapRule) };
  if (res?.data && typeof res.data === 'object') return { ...res, data: mapRule(res.data) };
  if (Array.isArray(res)) return res.map(mapRule);
  return res;
}

@Injectable({ providedIn: 'root' })
export class PremiumRuleService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/PremiumRule`;

  getAll() {
    return this.http.get(`${this.baseUrl}/GetAll`).pipe(map(mapEnvelope));
  }

  getById(id: number) {
    return this.http.get(`${this.baseUrl}/GetById`, { params: { id } }).pipe(map(mapEnvelope));
  }

  getByMasterPolicy(masterPolicyId: number) {
    return this.http
      .get(`${this.baseUrl}/GetByMasterPolicy`, { params: { masterPolicyId } })
      .pipe(map(mapEnvelope));
  }

  insert(dto: PremiumRuleDto) {
    return this.http.post(`${this.baseUrl}/Insert`, dto);
  }

  update(dto: PremiumRuleDto) {
    return this.http.post(`${this.baseUrl}/Update`, dto);
  }

  delete(id: number) {
    return this.http.get(`${this.baseUrl}/Delete`, { params: { id } });
  }
}
